import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useQuery } from "@tanstack/react-query";
import ApiCall from "../services/apiCall";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Hero from "../components/Hero";
import { queryClient } from "../services/queryClient";
import ConfirmModal from "../components/ConfirmModal";
import { BASE_URL } from "../services/apis";
import LoadingSpinner from "../components/LoadingSpinner";

const Profile = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [img, setImg] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ["api/user"],
  });

  const token = localStorage.getItem("userToken");
  const jsonToken = JSON.parse(token);

  const logOut = async () => {
    await handleLogOut();
    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
    navigate("/signin");
    queryClient.invalidateQueries();
  };

  const handleLogOut = async () => {
    try {
      const res = await ApiCall("api/logout", "POST", undefined);
      if (res.ok == true) {
        if (res?.message || res?.data?.message) {
          toast.success(res?.message || res?.data?.message);
        } else {
          toast.error("Something went wrong");
        }
      }
    } catch (error) {
      toast.error(error?.message);
    }
  };

  const handleImageChange = (e) => {
    setImg(e.target.files[0]);
  };

  const uploadImage = async () => {
    if (!img) {
      toast.error("Please select an image");
      return;
    }

    const token = localStorage.getItem("userToken");
    const jsonToken = JSON.parse(token);

    const formData = new FormData();
    formData.append("file", img);

    const headers = new Headers({
      Accept: "application/json",
    });

    if (jsonToken) {
      headers.append("Authorization", `Bearer ${jsonToken}`);
    }

    try {
      setLoading(true);
      const response = await fetch(BASE_URL + "api/user/avatar", {
        method: "POST",
        headers,
        body: formData,
      });
      const res = await response.json();
      if (response.ok) {
        toast.success("Profile image updated successfully");
        refetch(); // Refresh user data if needed
      } else {
        toast.error(res.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
      setImg(null);
    }
  };

  const deacctivateAcc = async () => {
    try {
      const res = await ApiCall("api/user/deactivate", "POST");
      if (res.ok) {
        toast.success("Account Deleted Successfully");
        logOut();
      } else {
        toast.error(
          typeof res?.message == "string"
            ? res?.message
            : "Something went wrong"
        );
      }
    } catch (error) {
      toast.error(error?.message);
    }
  };

  return (
    <Layout>
      <Hero title="Profile" />
      <section className="profile-sec section-gap">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-3">
              <div className="profile-pic-sec">
                <span className="rounded-full">
                  {loading ? (
                    <>
                      <LoadingSpinner />
                    </>
                  ) : (
                    <>
                      {data?.data?.avatarUrl ? (
                        <img src={data?.data?.avatarUrl} alt="Profile" />
                      ) : (
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z"></path>
                        </svg>
                      )}
                    </>
                  )}
                </span>
                <input
                  type="file"
                  onChange={handleImageChange}
                  onSubmit={(res) => console.log(res)}
                  style={{ display: "none" }}
                  id="profile-image-input"
                />
                <label htmlFor="profile-image-input">
                  <span className="edit-icon" onClick={uploadImage}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      data-slot="icon"
                    >
                      <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32L19.513 8.2Z"></path>
                    </svg>
                  </span>
                </label>
              </div>
            </div>
            <div className="col-lg-9">
              <div className="profile-details">
                <h3>
                  {data?.data?.firstName || data?.data?.lastName
                    ? `${data?.data?.firstName || ""} ${
                        data?.data?.lastName || ""
                      }`
                    : null}
                </h3>
                <p>
                  {data?.data?.email ? data?.data?.email : null}
                  <br />
                  {data?.data?.phoneNumber ? data?.data?.phoneNumber : null}
                </p>
              </div>
            </div>
          </div>
          <div className="account-setting-sec">
            <div className="row">
              <div className="col-lg-12">
                <div className="title-sec">
                  <h2 className="title-25">Account Settings</h2>
                </div>
                <ul className="setting-list">
                  {jsonToken ? (
                    <>
                      <li>
                        <Link
                          to="/edit-profile"
                          state={{
                            firstName: data?.data?.firstName,
                            lastName: data?.data?.lastName,
                            phoneNumber: data?.data?.phoneNumber,
                          }}
                        >
                          Edit Profile <i className="fas fa-chevron-right"></i>
                        </Link>
                      </li>
                      <li>
                        <Link to="/favourites">
                          Favourites <i className="fas fa-chevron-right"></i>
                        </Link>
                      </li>
                      <li>
                        <Link to="/favourite-clubs">
                          Favourite Clubs{" "}
                          <i className="fas fa-chevron-right"></i>
                        </Link>
                      </li>
                      <li>
                        <Link to="/favourite-events">
                          Favourite Events{" "}
                          <i className="fas fa-chevron-right"></i>
                        </Link>
                      </li>
                      <li>
                        <Link to="/favourite-artists">
                          Favourite Artists{" "}
                          <i className="fas fa-chevron-right"></i>
                        </Link>
                      </li>
                      <li>
                        <Link to="/reset-password">
                          Reset Password{" "}
                          <i className="fas fa-chevron-right"></i>
                        </Link>
                      </li>
                      <li>
                        <Link onClick={() => setIsDeleteModalOpen(true)}>
                          Delete Account{" "}
                          <i className="fas fa-chevron-right"></i>
                        </Link>
                      </li>
                    </>
                  ) : null}
                  <li>
                    <Link to="/terms_conditions">
                      Terms & Conditions{" "}
                      <i className="fas fa-chevron-right"></i>
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy_policy">
                      Privacy Policy <i className="fas fa-chevron-right"></i>
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact">
                      Contact Us <i className="fas fa-chevron-right"></i>
                    </Link>
                  </li>
                  <li>
                    <a href="http://api.whatsapp.com/send?phone=919372228386&text=Hey%20There%2C%0A%0AI%20am%20looking%20to%20get%20my%20club%20listed%20on%20the%20Azzir%20App">
                      Club Registration <i className="fas fa-chevron-right"></i>
                    </a>
                  </li>
                  <li>
                    <a href="http://api.whatsapp.com/send?phone=919372228386&text=Hey%20There%2C%0A%0AI%20am%20looking%20to%20get%20the%20artist%20registration%20%2F%20listing%20done%20on%20the%20AZZIR%20App">
                      Artist Registration{" "}
                      <i className="fas fa-chevron-right"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" onClick={logOut}>
                      Log Out <i className="fas fa-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>
      <a href="#" className="back-to-top">
        <i className="fa fa-chevron-up"></i>
      </a>
      <ToastContainer />
      <ConfirmModal
        title="Delete Account"
        content="Are you sure you want to delete your account?"
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deacctivateAcc}
      />
    </Layout>
  );
};

export default Profile;
